import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Chip, CircularProgress, IconButton, Paper, Typography } from "@mui/material";
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CifrasJornada, { colorDiferencia } from "./CifrasJornada";
import ChipFichaje, { ChipAnulado } from "./ChipFichaje";
import ExportarJornada from "./ExportarJornada";
import JornadaService from "../../services/JornadaService";
import LogService from "../../services/LogService";
import ILog from "../../types/Log";
import IAlert from "../../types/Alert";
import { IMisFichajes } from "../../types/Jornada";
import { diaSemanaCorto, diferenciaFichada, duracionFichada, horaFichaje, mesAnterior, mesDe, NOMBRE_FICHAJE, nombreMes, primerDia, resumenFichajes, siguienteMes, textoAnulado, ultimoDia } from "../../utils/Jornada";
import * as constantes from "../../common/Constantes";

// Pestaña «Mis fichajes»: los registros propios, mes a mes. También en solo lectura, para quien ya no ficha.
interface MisFichajesProps {
    setAlert: (alert: IAlert) => void;
    soloLectura?: boolean;
}

type Fallo = { message?: string; response?: { status?: number; data?: { message?: string } } };

const MisFichajes = ({ setAlert, soloLectura = false }: MisFichajesProps) => {
    const history = useNavigate();
    const [mes, setMes] = useState<string | null>(null); // null hasta saber el mes de hoy en la clínica
    const [mesActual, setMesActual] = useState<string | null>(null);
    const [datos, setDatos] = useState<IMisFichajes | null>(null);
    const [loading, setLoading] = useState(true);

    const cargar = useCallback((elegido: string | null) => {
        setLoading(true);
        // La primera vez se pide el mes del dispositivo y la API dice cuál es hoy en la clínica
        const hoyDispositivo = new Date();
        const consulta = elegido ?? `${hoyDispositivo.getFullYear()}-${String(hoyDispositivo.getMonth() + 1).padStart(2, '0')}`;
        JornadaService.getMisFichajes(primerDia(consulta), ultimoDia(consulta))
            .then((response: { data: IMisFichajes }) => {
                setDatos(response.data);
                setMesActual(mesDe(response.data.hoy));
                setMes(consulta);
            })
            .catch((e: unknown) => {
                const fallo = e as Fallo;
                if (fallo.response?.status === 401) {
                    localStorage.clear();
                    history(constantes.R_HOME);
                    return;
                }
                if (![400, 403, 404].includes(fallo.response?.status ?? 0)) {
                    const error: ILog = { method: 'MisFichajes.getMisFichajes', level: 'error', message: fallo.message ?? '', meta: fallo.response };
                    LogService.logError(error);
                }
                setAlert({ open: true, severity: 'error', msg: fallo.response?.data?.message || 'No se han podido cargar tus fichajes' });
            })
            .finally(() => setLoading(false));
    }, [history, setAlert]);

    useEffect(() => { cargar(null); }, [cargar]);

    // Los días con fichajes y los ya pasados con horas previstas sin fichajes (cuentan en el total, así que se ven); del más
    // reciente al más antiguo
    const dias = (datos?.dias ?? []).filter(d => d.eventos.length > 0 || d.anulados.length > 0 || (!!datos && d.fecha < datos.hoy && d.previstoMs > 0)).reverse();

    return (
        <Box>
            {soloLectura && (
                <Typography variant="body1" sx={{ mb: 2 }}>Ya no tienes que fichar. Aquí tienes tus registros anteriores.</Typography>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <IconButton aria-label="Mes anterior" disabled={loading || !mes} onClick={() => mes && cargar(mesAnterior(mes))}><ChevronLeftIcon /></IconButton>
                <Typography variant="h6" sx={{ minWidth: 180, textAlign: 'center' }}>{mes ? nombreMes(mes) : ''}</Typography>
                <IconButton aria-label="Mes siguiente" disabled={loading || !mes || mes === mesActual} onClick={() => mes && cargar(siguienteMes(mes))}><ChevronRightIcon /></IconButton>
                {/* Lo propio, también en solo lectura */}
                {mes && datos && <Box sx={{ ml: 'auto' }}><ExportarJornada mes={mes} propio setAlert={setAlert} /></Box>}
            </Box>

            {loading ? <CircularProgress /> : datos && (
                <>
                    {(() => {
                        // Totales de los días terminados (hoy cuenta cuando se cierra la jornada), para que cuadren entre sí
                        const hoyEnCurso = datos.dias.some(d => d.fecha === datos.hoy && d.estado !== 'cerrada');
                        const terminados = datos.dias.filter(d => d.fecha < datos.hoy || (d.fecha === datos.hoy && d.estado === 'cerrada'));
                        const diferenciaMs = terminados.reduce((s, d) => s + d.diferenciaMs, 0);
                        return (
                            <Box sx={{ mb: 2 }}>
                                <CifrasJornada cifras={[
                                    { etiqueta: 'Fichado', valor: duracionFichada(terminados.reduce((s, d) => s + d.trabajadoMs, 0)) },
                                    { etiqueta: 'Previsto', valor: duracionFichada(terminados.reduce((s, d) => s + d.previstoMs, 0)) },
                                    { etiqueta: 'Diferencia', valor: diferenciaFichada(diferenciaMs), color: colorDiferencia(diferenciaMs) },
                                ]} />
                                {mes === mesActual && (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                        {hoyEnCurso ? 'Hasta ayer: hoy cuenta al cerrar la jornada' : 'Hasta hoy'}
                                    </Typography>
                                )}
                            </Box>
                        );
                    })()}

                    {dias.length === 0 && <Typography variant="body2" color="text.secondary">No hay fichajes este mes.</Typography>}
                    {dias.map(d => (
                        <Paper variant="outlined" key={d.fecha} sx={{ px: 2, py: 1.5, mb: 1.5 }}>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
                                <Typography variant="subtitle1">{diaSemanaCorto(d.fecha, true)}</Typography>
                                {d.esFestivo && <Chip size="small" label="Festivo" />}
                                {d.estado === 'incompleta' && <Chip size="small" color="warning" label="Incompleta" />}
                                {d.eventos.length === 0 && <Chip size="small" variant="outlined" label="Sin fichajes" />}
                                {/* Sin fichajes o incompleto (no hay nada fichado que contar), solo lo previsto; si no, lo fichado frente
                                    a lo previsto */}
                                <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
                                    {d.eventos.length === 0 || d.estado === 'incompleta'
                                        ? (d.previstoMs > 0 ? `Previsto ${duracionFichada(d.previstoMs)}` : '')
                                        : duracionFichada(d.trabajadoMs)}
                                    {d.previstoMs > 0 && <>
                                        {d.eventos.length > 0 && d.estado !== 'incompleta' ? ` de ${duracionFichada(d.previstoMs)} · ` : ' · '}
                                        {datos.hoy === d.fecha && d.estado !== 'cerrada'
                                            ? (d.trabajadoMs < d.previstoMs ? `quedan ${duracionFichada(d.previstoMs - d.trabajadoMs)}` : diferenciaFichada(d.diferenciaMs))
                                            : <Box component="span" sx={{ color: colorDiferencia(d.diferenciaMs) }}>{diferenciaFichada(d.diferenciaMs)}</Box>}
                                    </>}
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
                                {resumenFichajes(d.eventos, datos.nombres).map((l, i) => <ChipFichaje key={i} linea={l} size="small" />)}
                                {/* Los anulados por el admin, tachados (lo deshecho por uno mismo no se muestra) */}
                                {d.anulados.map(a => <ChipAnulado key={a._id} size="small" texto={`${NOMBRE_FICHAJE[a.tipo]} ${horaFichaje(a.hora)}`} detalle={textoAnulado(a, datos.nombres)} />)}
                            </Box>
                        </Paper>
                    ))}
                </>
            )}
        </Box>
    );
};

export default MisFichajes;
